// client/src/api/quotes.js
const API_BASE_URL = '/api'; // Your backend API base URL

/**
 * Helper to get headers without authentication token.
 * @returns {object} Headers object.
 */
const getHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

/**
 * Recursively converts all object keys from camelCase to snake_case and removes unnecessary fields.
 * Handles nested objects and arrays.
 * @param {any} data - The data to transform.
 * @returns {any} The transformed data with snake_case keys and filtered fields.
 */
const camelToSnake = (data) => {
  if (Array.isArray(data)) {
    return data.map(item => camelToSnake(item));
  }
  if (data !== null && typeof data === 'object' && !(data instanceof Date)) {
    const newObject = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // Skip fields that should not be sent to the server
        if (['quote_id', 'created_at', 'updated_at', 'quote_section_id'].includes(key)) {
          continue;
        }
        const snakeKey = key.replace(/([A-Z])/g, (g) => `_${g.toLowerCase()}`);
        newObject[snakeKey] = (typeof data[key] === 'object' && data[key] !== null && !(data[key] instanceof Date)) 
                              ? camelToSnake(data[key]) 
                              : data[key];
      }
    }
    return newObject;
  }
  return data;
};

/**
 * Recursively converts all object keys from snake_case to camelCase.
 * Handles nested objects and arrays.
 * IMPORTANT: This version directly assigns simple values and only recurses for objects/arrays.
 * Date objects will be created on the frontend as needed.
 * @param {any} data - The data to transform.
 * @returns {any} The transformed data with camelCase keys.
 */
const snakeToCamel = (data) => {
  if (Array.isArray(data)) {
    return data.map(item => snakeToCamel(item));
  }
  // Only recurse if it's an object, not null, and not already a Date instance
  if (data !== null && typeof data === 'object' && !(data instanceof Date)) {
    const newObject = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        // Recursively apply transformation only if the value is an object or array
        newObject[camelKey] = (typeof data[key] === 'object' && data[key] !== null && !(data[key] instanceof Date)) 
                              ? snakeToCamel(data[key]) 
                              : data[key]; // Directly assign primitive values and Date objects
      }
    }
    return newObject;
  }
  return data; // Return non-objects (primitives, Dates) as they are
};

/**
 * Fetches all quotes for the authenticated user.
 * @param {object} [filters] - Optional filters (e.g., { status: 'draft', isArchived: false }).
 * @returns {Promise<Array>} A promise that resolves to an array of quote objects.
 */
export const fetchQuotes = async (filters = {}) => {
  const queryParams = new URLSearchParams(filters).toString();
  const url = `${API_BASE_URL}/quotes${queryParams ? `?${queryParams}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch quotes'); 
  }
  const data = await response.json();
  // Ensure 'quotes' is returned as an array, applying snakeToCamel to each item if data.quotes exists
  return { quotes: Array.isArray(data.quotes) ? snakeToCamel(data.quotes) : [] }; 
};

/**
 * Fetches a single quote with its sections and items.
 * @param {number} quoteId - The ID of the quote to fetch.
 * @returns {Promise<object>} A promise that resolves to the quote object with details.
 */
export const fetchQuoteById = async (quoteId) => {
  const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch quote details');
  }
  const data = await response.json();
  return { quote: snakeToCamel(data.quote) }; // Apply transformation
};

/**
 * Creates a new quote.
 * @param {object} quoteData - Object containing full quote details (client, name, sections, regularLineItems, etc.).
 * @returns {Promise<object>} A promise that resolves to the new quote's info.
 */
export const createQuote = async (quoteData) => {
  const response = await fetch(`${API_BASE_URL}/quotes`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(quoteData),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create quote');
  }
  const data = await response.json();
  return { quote: snakeToCamel(data.quote) }; // Apply transformation to returned new quote
};

/**
 * Updates an existing quote.
 * @param {number} quoteId - The ID of the quote to update.
 * @param {object} quoteData - Object containing updated quote details (including regularLineItems).
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const updateQuote = async (quoteId, quoteData) => {
  console.log('Sending updated quote data:', JSON.stringify(quoteData, null, 2));
  const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(quoteData), // Send quoteData directly
    credentials: 'include',
  });


  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update quote');
  }
  return response.json(); // No data transformation needed for simple success message
};

/**
 * Updates the status or archive state of a quote.
 * @param {number} quoteId - The ID of the quote to update.
 * @param {object} updateData - Object with status or isArchived boolean.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const updateQuoteStatusOrArchive = async (quoteId, updateData) => {
    const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}/status`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(updateData),
        credentials: 'include',
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update quote status.');
    }
    return response.json();
};


/**
 * Duplicates a quote.
 * @param {number} quoteId - The ID of the quote to duplicate.
 * @returns {Promise<object>} A promise that resolves to the new duplicated quote's info.
 */
export const duplicateQuote = async (quoteId) => {
  const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}/duplicate`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to duplicate quote');
  }
  const data = await response.json();
  return { quote: snakeToCamel(data.quote) }; // Apply transformation to returned new quote
};

/**
 * Deletes a quote.
 * @param {number} quoteId - The ID of the quote to delete.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const deleteQuote = async (quoteId) => {
  const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete quote');
  }
  return response.json();
};

// client/src/api/quotes.js

/**
 * Sends a quote email with a link to the public quote page.
 * @param {number} quoteId - The ID of the quote to send.
 * @param {object} emailData - Object containing recipientEmail, subject, and the full bodyText (which should include the public link).
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const sendQuoteLinkByEmail = async (quoteId, emailData) => {
  // The backend endpoint URL can stay the same. We will change what the backend DOES at this endpoint.
  const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}/send-email`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(emailData),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to send quote email');
  }
  return response.json();
};

/**
 * Archives a quote.
 * @param {number} quoteId - The ID of the quote to archive.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const archiveQuote = async (quoteId) => {
  const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}/archive`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to archive quote');
  }
  return response.json();
};

/**
 * Unarchives a quote.
 * @param {number} quoteId - The ID of the quote to unarchive.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const unarchiveQuote = async (quoteId) => {
  const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}/unarchive`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to unarchive quote');
  }
  return response.json();
};

// --- Public Access Quote APIs (No Auth Required) ---

/**
 * Fetches a quote for public viewing using its public access token.
 * @param {string} publicAccessToken - The unique token for public access.
 * @returns {Promise<object>} A promise that resolves to the quote object for display.
 */
export const fetchPublicQuote = async (publicAccessToken) => {
    const response = await fetch(`${API_BASE_URL}/quotes/public/${publicAccessToken}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Quote not found or inaccessible.');
    }
    const data = await response.json();
    return { quoteData: snakeToCamel(data.quoteData) }; // Apply transformation
};

/**
 * Records client acceptance of a public quote.
 * @param {string} publicAccessToken - The unique token for public access.
 * @param {object} clientAcceptanceData - Data including clientName and confirmation.
 * @returns {Promise<object>} A promise that resolves to a success message.
 */
export const signPublicQuote = async (publicAccessToken, clientAcceptanceData) => {
    const response = await fetch(`${API_BASE_URL}/quotes/public/${publicAccessToken}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clientAcceptanceData),
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to sign quote.');
    }
    return response.json();
};
